import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useEffect, useState } from 'react'
import { View } from 'react-native'
import { appHeaderState$ } from '~/components/AppHeader'
import { FeedbackFlow } from '~/components/feedback/FeedbackFlow'
import Icon from '~/components/Icon'
import { useAppView } from '~/components/layout/AppViewContext'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { SectionContent } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import RealtorCard from '~/components/realtors/RealtorCard'
import { Heading2 } from '~/components/typography/Titles'
import { LinkButton, ButtonLabel } from '~/components/ui/button'
import { useSession } from '~/components/user/SessionProvider'
import { useInterest } from '~/data/interests/useInterests'
import { useUserProfile } from '~/data/user/userProfile'
import { ConfettiTrigger } from '~/lib/ConfettiProvider'
import { trpc } from '~/lib/trpc'

export default function RealtorLink() {
  const { setBackgroundTheme } = useAppView()

  useEffect(() => {
    setBackgroundTheme(0)
  }, [setBackgroundTheme])

  useEffect(() => {
    appHeaderState$.restrictedStates.set(() => new Set(['back', 'menu', 'onboarding']))

    return () => {
      appHeaderState$.restrictedStates.clear()
    }
  }, [])

  return (
    <ScreenView>
      <RealtorLinkContent />
    </ScreenView>
  )
}

function RealtorLinkContent() {
  const { t } = useTranslations('feedback')
  const { realtorSlug } = useLocalSearchParams<{ realtorSlug: string }>()
  const router = useRouter()
  const [isComplete, setIsComplete] = useState(false)

  const { session, isAnonymous, isLoading: isSessionLoading } = useSession()
  const { data: userProfile, isLoading: isUserProfileLoading } = useUserProfile()
  const { data: interest } = useInterest()

  const user = session?.user ?? null
  const isAuthenticatedUser = !!user && !isAnonymous
  const decodedRealtorSlug = decodeURIComponent(realtorSlug ?? '')

  const { data: realtorContext, isLoading } = useQuery(
    trpc.feedback.realtorLinkContext.queryOptions(
      { slug: decodedRealtorSlug },
      { enabled: !!realtorSlug }
    )
  )

  if (isLoading || (isAuthenticatedUser && (isSessionLoading || isUserProfileLoading))) {
    return <LoadingView />
  }

  if (!realtorContext) {
    return (
      <ContentBox contentContainerClassName="gap-6">
        <View className="items-center self-center">
          <Icon name="alert-circle" size={48} className="text-destructive" />
        </View>
        <Heading2 className="text-center font-bold text-2xl">
          {t(($) => $.realtorLink.notFoundTitle)}
        </Heading2>
        <SectionContent>{t(($) => $.realtorLink.notFoundDescription)}</SectionContent>
        <LinkButton variant="primary" href="/">
          <ButtonLabel>{t(($) => $.flow.back)}</ButtonLabel>
        </LinkButton>
      </ContentBox>
    )
  }

  if (isComplete) {
    return (
      <ContentBox contentContainerClassName="relative gap-6">
        <ConfettiTrigger
          confettiKey="realtor-link-complete"
          className="mb-2 size-[64px] items-center self-center">
          <Icon name="checkmark-circle" size={64} className="text-secondary" />
        </ConfettiTrigger>
        <Heading2 className="text-center font-bold text-3xl">
          {t(($) => $.realtorLink.successTitle)}
        </Heading2>
        <SectionContent textClassName="text-pretty text-lg/normal">
          {t(($) => $.realtorLink.successDescription)}
        </SectionContent>
        <View className="mt-4 items-center gap-4 self-stretch rounded-lg bg-muted p-4 px-6 text-center">
          <RealtorCard userId={realtorContext.userId} showContactInfo />
        </View>
      </ContentBox>
    )
  }

  return (
    <FeedbackFlow
      flowId={`realtor-link-${realtorContext.userId}`}
      realtorUserId={realtorContext.userId}
      initialContactDetails={
        isAuthenticatedUser
          ? {
              name: userProfile?.name ?? '',
              email: user?.email ?? undefined,
              phone: user?.phone ?? undefined,
            }
          : undefined
      }
      initialInterestData={interest?.data ?? null}
      flowTitle={t(($) => $.realtorLink.flowTitle)}
      onComplete={(outcome) => {
        if (outcome === 'completed') {
          setIsComplete(true)
        }
      }}
      onClose={() => router.back()}
    />
  )
}
